/**
 * frontend/src/types.ts
 * =====================
 * Tipos compartidos entre todos los módulos del frontend.
 * Espejo de los schemas Pydantic del backend (src/api/schemas.py).
 *
 * Principio de independencia:
 *   Si el backend cambia un campo, el único lugar a actualizar es este archivo.
 */

// ── Estados posibles de una sesión ───────────────────────────────────────────

export type EstadoConciliacion =
  | "procesando"
  | "esperando_aprobacion"
  | "completado"
  | "cancelado"
  | "error";

// ── Vistas de la SPA (mapeo 1:1 con sub-issues de Epic 6) ────────────────────

export type Vista =
  | "upload"               // Sub-issue 1: input screen
  | "procesando"           // Sub-issue 2: conciliación en curso
  | "enviando"             // post-HITL: generando y enviando reporte
  | "esperando_aprobacion" // Sub-issue 4: HITL panel
  | "completado"           // Sub-issue 4: resultado exitoso
  | "cancelado"            // Sub-issue 4: cancelación limpia
  | "error";               // Sub-issue 2: error tipificado

// ── Preferencias del usuario ─────────────────────────────────────────────────

export interface Preferences {
  /** Plantilla del nombre del archivo de salida. Variables: {cuenta}, {periodo}, {fecha} */
  nombreArchivoTemplate: string;

  /** Nombre de la empresa/organización (se muestra en reportes) */
  nombreEmpresa: string;

  /** Emails de destinatarios del reporte, separados por comas */
  emailDestinatarios: string;

  /** Tolerancia de fechas para el matching de transacciones (días) */
  toleranciaFechasDias: number;

  /** Diferencias de saldo <= a este monto no se reportan como diferencia (0 = desactivado) */
  umbralDiferenciaIgnorable: number;

  /** Código/número de cuenta del Libro Mayor a conciliar */
  codigoCuentaLibroMayor: string;

  // ── Futuro: Drive OAuth 2.0 ────────────────────────────────────────────────
  /** Nombre descriptivo de la carpeta Drive de entrada (configurable via OAuth, próximamente) */
  driveFolderInputNombre: string;

  /** Nombre descriptivo de la carpeta Drive de salida (configurable via OAuth, próximamente) */
  driveFolderOutputNombre: string;
}

export const DEFAULT_PREFERENCES: Preferences = {
  nombreArchivoTemplate: "Conciliacion_Bancaria_{cuenta}_{periodo}",
  nombreEmpresa: "",
  emailDestinatarios: "",
  toleranciaFechasDias: 1,
  umbralDiferenciaIgnorable: 0,
  codigoCuentaLibroMayor: "",
  driveFolderInputNombre: "",
  driveFolderOutputNombre: "",
};

// ── Estado global de la app ──────────────────────────────────────────────────

export interface AppState {
  vista: Vista;
  threadId: string | null;
  resumen: string | null;
  errores: string[];
  periodoLabel: string;
  driveUrlExcel: string | null;
  driveUrlPdf: string | null;
}

// ── Schema Mapping ───────────────────────────────────────────────────────────

export type TipoFuente   = "extracto" | "libro_mayor";
export type TipoImporte  = "columna_unica" | "debe_haber" | "monto_tipo";
export type FormatoNumerico = "punto_miles_coma_dec" | "coma_miles_punto_dec" | "sin_sep_coma_dec" | "sin_sep_punto_dec";
/**
 * Espejo del enum FormatoFecha del backend (src/parsers/schema/column_profile.py).
 * Los literales son los valores que viajan por la API y se persisten en los
 * perfiles guardados: se pueden agregar, pero renombrar uno rompe los perfiles
 * que ya lo usan.
 */
export type FormatoFecha =
  | "dmy_slash"       // DD/MM/YYYY
  | "dmy_dash"        // DD-MM-YYYY
  | "dmy_dot"         // DD.MM.YYYY
  | "dmy_short"       // DD/MM/YY
  | "dmy_dash_short"  // DD-MM-YY
  | "ymd_iso"         // YYYY-MM-DD
  | "mdy_slash"       // MM/DD/YYYY
  | "mdy_dash_short"  // MM-DD-YY
  | "excel_serial";   // número de serie de Excel

export interface ColumnProfile {
  id: string;
  nombre: string;
  tipo_fuente: TipoFuente;
  banco_o_erp: string;
  creado_en: string;
  col_fecha: string;
  col_descripcion: string;
  tipo_importe: TipoImporte;
  col_monto?: string | null;
  col_debe?: string | null;         // columna POSITIVA (Crédito/Debe)
  col_haber?: string | null;        // columna NEGATIVA (Débito/Haber)
  col_monto_val?: string | null;
  col_monto_tipo?: string | null;
  valor_credito: string;
  col_saldo?: string | null;
  col_referencia?: string | null;
  col_cuenta?: string | null;
  codigo_cuenta?: string | null;   // valor del código de cuenta a filtrar (solo libro_mayor)
  /** Solo libro mayor: el archivo ya trae solo la cuenta a conciliar; reemplaza a cuenta + código. */
  cuenta_unica?: boolean;
  formato_numerico: FormatoNumerico;
  formato_fecha: FormatoFecha;
  fila_inicio_datos: number;
  /**
   * Tolerancia de fechas del matching, en días (0–31). Es un rasgo de la
   * cuenta, por eso vive en el perfil y no en Preferencias; en una
   * conciliación rige la mayor entre los dos perfiles.
   */
  tolerancia_dias?: number;
  hoja_excel?: string | null;
  encoding: string;
  delimitador_csv?: string | null;  // null / undefined = auto-detectar
}

export interface SugerenciaColumna {
  columna: string | null;
  confianza: number;
}

// ── Detalle de la conciliación (pantalla de revisión) ────────────────────────
// Espejo de `src/report/detalle.py`: las mismas columnas que las hojas del Excel.

export interface MovimientoDetalle {
  fecha: string;
  descripcion: string;
  monto: number | null;
  asiento?: string;
}

export interface ParConciliado {
  e_fecha: string;
  e_descripcion: string;
  e_monto: number | null;
  l_fecha: string;
  l_asiento: string;
  l_descripcion: string;
  l_monto: number | null;
  /** true si el par se aceptó con corrimiento de fecha. */
  tolerancia: boolean;
}

export interface GrupoConciliado {
  tipo: string;
  monto_total: number;
  extracto: MovimientoDetalle[];
  libro: MovimientoDetalle[];
}

export interface PeriodoDetalle {
  periodo: string;
  estado: string;
  saldo_banco: number;
  saldo_mayor: number;
  conciliados: ParConciliado[];
  grupos: GrupoConciliado[];
  tipo_a: MovimientoDetalle[];
  tipo_b: MovimientoDetalle[];
  totales: { conciliados: number; grupos: number; tipo_a: number; tipo_b: number };
}

export interface ResultadoDeteccion {
  columnas_disponibles: string[];
  col_fecha: SugerenciaColumna;
  col_descripcion: SugerenciaColumna;
  col_monto: SugerenciaColumna;
  col_debe: SugerenciaColumna;
  col_haber: SugerenciaColumna;
  col_saldo: SugerenciaColumna;
  col_referencia: SugerenciaColumna;
  col_cuenta: SugerenciaColumna;
  formato_numerico: FormatoNumerico | null;
  formato_fecha: FormatoFecha | null;
  /**
   * ¿El formato configurado cambia cómo se lee ESTE archivo?
   *
   * Los XLSX guardan fechas e importes con tipo propio y llegan ya convertidos:
   * ahí el formato no interviene y el wizard oculta el selector en vez de
   * ofrecer un control sin efecto.
   */
  formato_fecha_aplica: boolean;
  formato_numerico_aplica: boolean;
  fila_inicio_datos: number;
  hojas_disponibles: string[];
  /** Primeras filas del archivo: el usuario elige el encabezado viéndolas. */
  filas_preview: string[][];
  total_filas: number;
  fecha_min: string | null;
  fecha_max: string | null;
  advertencias: string[];
}

export interface RespuestaDeteccion {
  deteccion: ResultadoDeteccion;
  perfil_sugerido: Partial<ColumnProfile>;
}

export interface ColumnProfileResponse {
  id: string;
  nombre: string;
  tipo_fuente: TipoFuente;
  banco_o_erp: string;
  creado_en: string;
  detalle: ColumnProfile;
}

/**
 * Cómo el usuario eligió mapear las columnas de un archivo.
 *
 *   guardado → perfil persistido; se envía el id y el backend lo busca
 *   inline   → borrador del wizard sin guardar; se envía el perfil completo
 *   null     → sin elegir; conciliar queda deshabilitado
 *
 * Unión discriminada para que el compilador obligue a contemplar ambos casos
 * y no se pueda enviar un borrador como si fuera un perfil guardado.
 *
 * `id` es lo que viaja al backend. `nombre` va solo para mensajes y logs: es
 * una etiqueta que el usuario puede repetir entre perfiles —"Santander" para
 * el extracto y para el libro mayor es lo natural cuando ambos archivos salen
 * del mismo banco— y por lo tanto no sirve para identificar.
 */
export type SeleccionPerfil =
  | { tipo: "guardado"; id: string; nombre: string }
  | { tipo: "inline"; perfil: Partial<ColumnProfile> };

// ── Responses del backend ────────────────────────────────────────────────────

export interface RespuestaIniciar {
  thread_id: string;
  status: EstadoConciliacion;
}

export interface RespuestaEstado {
  status: EstadoConciliacion;
  resumen: string | null;
  errores: string[];
}

export interface RespuestaResultado {
  periodo_label: string;
  drive_url_excel: string | null;
  drive_url_pdf: string | null;
}
